import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquare, RefreshCw } from 'lucide-react';

import {
  messagingKeys,
  useConversationListQuery,
  useConversationMessagesQuery,
} from '@/client/api/messaging/messaging.query';
import type {
  AdminMessage,
  ConversationRegistry,
  MessagingPartySummary,
} from '@/client/types/messaging';
import ProfileImage from '@/client/components/common/ProfileImage';
import { Skeleton } from '@/components/ui/skeleton';

const fmtTs = (t?: number | null) =>
  t ? new Date(t < 1e12 ? t * 1000 : t).toLocaleString() : '';

const nameOf = (s?: MessagingPartySummary | null) =>
  s
    ? s.name ??
      s.preferred_name ??
      [s.first_name, s.last_name].filter(Boolean).join(' ') ??
      s.username ??
      (s.uid ? `uid ${s.uid}` : 'Unknown')
    : 'Unknown';

/** The other party in a conversation, from the summaries map (skip this user). */
const otherParty = (conv: ConversationRegistry, uid: number): MessagingPartySummary | null => {
  const summaries = conv.user_summaries ?? {};
  const entries = Object.entries(summaries);
  const other = entries.find(([key, s]) => key !== String(uid) && s.uid !== uid);
  return other?.[1] ?? entries[0]?.[1] ?? null;
};

const MessagesTab = ({ uid }: { uid: number }) => <ChatView uid={uid} />;

const ChatView = ({ uid }: { uid: number }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useConversationListQuery({ uid });
  const conversations = data?.conversations ?? [];
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Refetches both the conversation list and the open thread — both live under
  // the `conversations` key.
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });

  // Default to the most recent conversation once the list loads.
  useEffect(() => {
    if (!activeKey && conversations.length > 0) {
      setActiveKey(conversations[0].conversation_key);
    }
  }, [activeKey, conversations]);

  const activeConv = conversations.find(c => c.conversation_key === activeKey) ?? null;

  return (
    <div className="grid h-[36rem] grid-cols-1 overflow-hidden rounded-lg border border-gray-200 bg-white md:grid-cols-[18rem_1fr]">
      <div className="flex min-h-0 flex-col border-b border-gray-200 md:border-b-0 md:border-r">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Conversations
          </span>
          <button
            type="button"
            onClick={refresh}
            disabled={isFetching}
            title="Refresh messages"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-400">No conversations.</p>
        ) : (
          conversations.map(conv => {
            const other = otherParty(conv, uid);
            const active = conv.conversation_key === activeKey;
            return (
              <button
                key={conv.conversation_key}
                type="button"
                onClick={() => setActiveKey(conv.conversation_key)}
                className={`flex w-full items-center gap-3 border-b border-gray-100 px-3 py-3 text-left transition-colors ${
                  active ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="h-9 w-9 flex-shrink-0">
                  <ProfileImage name={nameOf(other)} src={other?.photo_url ?? undefined} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{nameOf(other)}</p>
                  <p className="truncate text-xs text-gray-400">{fmtTs(conv.last_message_ts)}</p>
                </div>
              </button>
            );
          })
        )}
        </div>
      </div>

      <div className="flex min-h-0 flex-col">
        {activeConv ? (
          <Thread conversationKey={activeConv.conversation_key} uid={uid} title={nameOf(otherParty(activeConv, uid))} />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-400">
            <MessageSquare className="h-8 w-8" />
            <p className="text-sm">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Thread = ({
  conversationKey,
  uid,
  title,
}: {
  conversationKey: string;
  uid: number;
  title: string;
}) => {
  const { data, isLoading } = useConversationMessagesQuery(conversationKey, { limit: 100 });
  // API returns newest-first for some sorts; render oldest→newest for a chat feel.
  const messages: AdminMessage[] = [...(data?.messages ?? [])].sort(
    (a, b) => (a.timestamp ?? a.created_at ?? 0) - (b.timestamp ?? b.created_at ?? 0)
  );

  return (
    <>
      <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="font-mono text-[11px] text-gray-400">{conversationKey}</p>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-12 w-2/3" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">No messages in this conversation.</p>
        ) : (
          messages.map(m => {
            const mine = String(m.sender_uid) === String(uid);
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                    mine
                      ? 'rounded-br-sm bg-blue-600 text-white'
                      : 'rounded-bl-sm bg-white text-gray-900 ring-1 ring-gray-200'
                  }`}
                >
                  {m.redacted || m.is_deleted ? (
                    <em className={mine ? 'text-blue-100' : 'text-gray-400'}>message removed</em>
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{m.message ?? '—'}</span>
                  )}
                  <span
                    className={`mt-1 block text-right text-[10px] ${
                      mine ? 'text-blue-100' : 'text-gray-400'
                    }`}
                  >
                    {fmtTs(m.timestamp ?? m.created_at)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default MessagesTab;
